import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Cookie } from "lucide-react";
import { useStaticTranslation } from "@/hooks/useTranslation";
import { detectConsentRegion, getStoredConsent, setAnalyticsConsent } from "@/lib/analytics";

const OPEN_EVENT = "beedigit:open-cookie-preferences";

/** Re-opens the cookie banner so a visitor can change their choice. */
export const openCookiePreferences = () => {
  window.dispatchEvent(new CustomEvent(OPEN_EVENT));
};

const CookieConsent = () => {
  const [visible, setVisible] = useState(false);
  const { t } = useStaticTranslation();

  useEffect(() => {
    const reopen = () => setVisible(true);
    window.addEventListener(OPEN_EVENT, reopen);
    return () => window.removeEventListener(OPEN_EVENT, reopen);
  }, []);

  useEffect(() => {
    if (getStoredConsent()) return;

    let cancelled = false;
    let timer: number | undefined;
    // The banner is shown only in regions that require prior consent.
    // Region lookup failures count as consent-required (fail closed).
    void detectConsentRegion().then((required) => {
      if (cancelled || !required || getStoredConsent()) return;
      // Defer until the browser is idle so the banner never competes with
      // the hero for Largest Contentful Paint.
      const show = () => {
        timer = window.setTimeout(() => {
          if (!cancelled) setVisible(true);
        }, 1200);
      };
      const ric = (window as unknown as {
        requestIdleCallback?: (cb: () => void, o?: { timeout: number }) => void;
      }).requestIdleCallback;
      if (ric) ric(show, { timeout: 4000 });
      else timer = window.setTimeout(show, 2000);
    });

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, []);

  const accept = () => {
    setAnalyticsConsent("accepted");
    setVisible(false);
  };

  const decline = () => {
    setAnalyticsConsent("declined");
    setVisible(false);
  };

  if (!visible) return null;

  const current = getStoredConsent();

  return (
    <div className="fixed bottom-4 right-4 z-50 max-w-sm animate-in slide-in-from-bottom-4 duration-500">
      <div className="border border-border bg-card rounded-lg p-4 shadow-xl space-y-3">
        <div className="flex items-center gap-2">
          <Cookie className="h-5 w-5 text-primary shrink-0" />
          <h3 className="text-sm font-bold font-heading">{t("Cookie Consent")}</h3>
        </div>
        <p className="text-xs text-muted-foreground leading-relaxed">
          {t("We use cookies for analytics and advertising. They load only if you accept, and you can change your choice at any time in Cookie preferences.")}
        </p>
        {current && (
          <p className="text-xs text-muted-foreground">
            {current === "accepted" ? t("Current choice: analytics and advertising accepted.") : t("Current choice: analytics and advertising declined.")}
          </p>
        )}
        <div className="flex justify-end gap-2">
          <Button variant="outline" size="sm" onClick={decline}>
            {t("Decline")}
          </Button>
          <Button size="sm" onClick={accept}>
            {t("Accept")}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default CookieConsent;
